/**
 * Your data (2026-09-29): export everything, import, restore deleted items,
 * or close the workspace. Replaces a "Coming soon" placeholder; every action
 * here uses an endpoint that already existed.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Download, Upload, Trash2, RotateCcw } from 'lucide-react'

import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { enterpriseTrustService } from '../../services/enterpriseTrust'
import { apiErrorMessage } from '../../utils/apiError'

export function WorkspaceDataSection({ onOpenDeletion }: { onOpenDeletion: () => void }) {
  const navigate = useNavigate()
  const [exporting, setExporting] = useState(false)

  const exportAll = async () => {
    setExporting(true)
    try {
      const blob = await enterpriseTrustService.exportTenant()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `karyva-export-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Export downloaded.')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Export failed.'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="mb-1 text-2xl text-foreground">Your data</h1>
        <p className="text-muted-foreground">It's yours: take it out, bring it in, or get back what was deleted.</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Download className="h-5 w-5" /> Export everything</CardTitle>
            <CardDescription>A complete JSON copy of the workspace: portfolios, programs, projects, tasks, people and settings.</CardDescription>
          </CardHeader>
          <CardContent><Button onClick={exportAll} disabled={exporting} className="min-h-[44px]">{exporting ? 'Preparing…' : 'Download export'}</Button></CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Upload className="h-5 w-5" /> Import a spreadsheet</CardTitle>
            <CardDescription>Projects and tasks from Excel or CSV, with a preview before anything is saved.</CardDescription>
          </CardHeader>
          <CardContent><Button variant="outline" onClick={() => navigate('/import')} className="min-h-[44px]">Open import</Button></CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><RotateCcw className="h-5 w-5" /> Recycle bin</CardTitle>
            <CardDescription>Restore deleted portfolios, programs, projects and tasks.</CardDescription>
          </CardHeader>
          <CardContent><Button variant="outline" onClick={() => navigate('/recycle-bin')} className="min-h-[44px]">Open recycle bin</Button></CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-destructive"><Trash2 className="h-5 w-5" /> Close this workspace</CardTitle>
            <CardDescription>Schedules permanent deletion of all data after a grace period. You can cancel until then.</CardDescription>
          </CardHeader>
          <CardContent><Button variant="outline" onClick={onOpenDeletion} className="min-h-[44px]">Review deletion options</Button></CardContent>
        </Card>
      </div>
    </div>
  )
}
